import {del, get, post, put} from '@/services/api'

const state = {
    currentLocation: null,
    locations: [],
    newLocation: {
        id: null,
        augmentedRealityModel: null,
        localizedAttributes: {
            cz: {
                name: "",
                promptDescription: "",
                mainDescription: "",
                interests: ""
            },
            en: {
                name: "",
                promptDescription: "",
                mainDescription: "",
                interests: ""
            }
        },
        position: null,
        locationType: {
            id: 'ic_id_0'
        },
        scanningType: {
            id: 'ic_id_qr'
        },
        testQuestion: null,
        beacon: null,
        beaconId: null,
        url: null,
    },
    newQuestion: {
        id: null,
        points: 10,
        hidden: false,
        type: 1,
        localizedAttributes: {
            cz: {
                text: "",
            },
            en: {
                text: "",
            }
        },
    },
    newAnswer: {
        id: null,
        questionId: null,
        correct: false,
        localizedAttributes: {
            cz: {
                text: "",
            },
            en: {
                text: "",
            }
        },
    },
    locationHeaders: [
        {
            title: 'Název lokace',
            align: 'start',
            sortable: true,
            key: 'name',
            width: "22%",
            filterable: true
        },
        {
            title: 'Typ lokace',
            align: 'start',
            sortable: true,
            key: 'locationType',
            width: "17%",
            filterable: true
        },
        {
            title: 'Počet bodů',
            align: 'start',
            sortable: true,
            key: 'points',
            width: "10%",
            filterable: true
        },
        {
            title: 'AR model',
            align: 'start',
            sortable: true,
            key: 'augmentedRealityModelAvailability',
            width: "10%",
            filterable: true
        },
        {
            title: 'Typ skenování',
            align: 'start',
            sortable: true,
            key: 'scanningType',
            width: "17%",
            filterable: true
        },
        {
            title: 'Krátký popis',
            align: 'start',
            sortable: true,
            key: 'promptDescription',
            width: "24%",
            filterable: true
        },
    ],
    locationImages: [],
    locationModels: {
        android: [],
        ios: [],
    },
    didLoadLocation: false,
    didLoadLocations: false,
    locationTypes: [],
    scanningTypes: [],
    didLoadOnboardingUtils: false,
};

const getters = {
    getLocation: state => state.currentLocation,
    newLocation: state => state.newLocation,
    locations: state => state.locations,
    locationById: state => id => {
        return state.locations.find(l => l.id === id)
    },
    didLoadLocation: state => state.didLoadLocation,
    didLoadLocations: state => state.didLoadLocations,
    getLocationHeaders: state => state.locationHeaders,
    getLocationImages: state => state.locationImages,
    getIosLocationModels: state => state.locationModels.ios,
    getAndroidLocationModels: state => state.locationModels.android,
    getNewQuestion: state => state.newQuestion,
    getNewAnswer: state => state.newAnswer,
    getLocationTypes: state => state.locationTypes,
    getScanningTypes: state => state.scanningTypes,
};

function locationWithBeaconId(location){
    if(location.beacon !== null){
        location.beaconId = location.beacon.id
        return location
    } else {
        location.beaconId = null
        return location
    }
}

const actions = {
    prepareNewLocation({ commit }) {
        commit('clearLocation');
        commit('setLocation', JSON.parse(JSON.stringify(state.newLocation)));
    },

    async fetchLocations({commit}) {
        commit('setDidLoadLocations', false);
        await get('onboarding/locations/').then((response) => {
            commit('setLocations', response.data.items);
            commit('setDidLoadLocations', true);
        });
    },

    async fetchLocationById({commit}, id) {
        commit('errors/clear', null, { root: true });
        commit('setDidLoadLocation', false);
        await Promise.all([
            get(`onboarding/locations/web/${id}`),
            get(`onboarding/locations/images/${id}`),
        ]).then((response) => {
            commit('setLocation', locationWithBeaconId(response[0].data.content));
            commit('setImages', response[1].data.items);
            commit('setDidLoadLocation', true);
        }).catch(e => {
            if (e.statusCode === 404) {
                commit('errors/objectNotFound', null, {root: true});
            } else {
                throw e;
            }
        })
    },
    async fetchImages({commit}, id) {
        try {
            const response = await get(`onboarding/locations/images/${id}`)
            commit('setImages',response.data.items)
        } catch (e) {
            console.error(e);
        }
    },
    async fetchNeededOnboardingUtils({commit}) {
        commit('setDidLoadOnboardingUtils', false);
        await Promise.all([
            get('onboarding/utils/loc_types'),
            get('onboarding/utils/scn_types'),
        ]).then((response) => {
            commit('setLocationTypes', response[0].data.items)
            commit('setScanningTypes', response[1].data.items)
            commit('setDidLoadOnboardingUtils', true)
        }).catch(e => {
            if (e.statusCode === 404) {
                commit('errors/objectNotFound', null, {root: true});
            } else {
                throw e;
            }
        })
    },

    async createLocation({commit}, {location}) {
        const response = await post(`onboarding/locations/`, location);
        const newLocation =  locationWithBeaconId(response.data.content);
        commit('addLocation', newLocation);
        return newLocation.id;
    },
    async updateLocation({commit}, {location}) {
        const response = await put(`onboarding/locations/web/${location.id}`, location);
        commit('setLocation', locationWithBeaconId(response.data.content));
    },

    async deleteLocation({ commit },  id) {
        await del( `onboarding/locations/${id}`)
            .then(() => {
                commit('updateLocations', {id});
            });
    },
};

const mutations = {
    addLocation: (state, item) => (state.locations.push(item)),
    setLocation: (state, location) => {

        state.currentLocation = location
        state.locationModels.android = []
        state.locationModels.ios = []
        let ids = []

        if(location.augmentedRealityModelAvailability){
            ids = Object.keys(location.augmentedRealityModelAvailability)
        }

        ids.forEach((id) => {
            let AR = location.augmentedRealityModelAvailability?.[id]
            if (Object.keys(AR)[0] === 'android'){
                state.locationModels.android = [{
                    id: parseInt(id),
                    name: AR.android,
                }]
            } else if(Object.keys(AR)[0] === 'ios'){
                state.locationModels.ios = [{
                    id: parseInt(id),
                    name: AR.ios,
                }]
            }
        })
    },
    setLocations: (state, locations) => (state.locations = locations),
    setAugmentedReality: (state, location) => {
        state.currentLocation.augmentedRealityModel = location.augmentedRealityModel;
        state.currentLocation.augmentedRealityModelAvailability = location.augmentedRealityModelAvailability;
    },
    clearLocation: (state) => {
        state.currentLocation = null;
    },
    updateLocation: (state, item) => {
        const existing = state.locations.findIndex(l => l.id === item.id);
        if (existing > -1) {
            state.locations.splice(existing, 1, item);
        } else {
            state.locations.push(item);
        }
    },
    updateLocations: (state, {id}) => {
        let locations = [];
        state.locations.forEach( (item) => {
            if ( item.id !== id ){
                locations.push(item);
            }
        });
        state.locations = locations;
    },
    addImage: (state, image) => (state.locationImages.push(image)),
    setImages: (state, images) => state.locationImages = images,
    setIosLocationModels: (state, files) => state.locationModels.ios = files,
    setAndroidLocationModels: (state, files) => state.locationModels.android = files,
    removeImage: (state, id) => state.locationImages = state.locationImages.filter(i => i.id !== id),
    setDidLoadLocation: (state, value) => (state.didLoadLocation = value),
    setDidLoadLocations: (state, value) => state.didLoadLocations = value,
    setLocationTypes: (state, types) => state.locationTypes = types,
    setScanningTypes: (state, types) => state.scanningTypes = types,
    setDidLoadOnboardingUtils: (state, value) => state.didLoadOnboardingUtils = value,
    removeModel: (state, { platform, id }) => state.locationModels[platform] = state.locationModels[platform].filter(f => f.id !== id)
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
